import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { FautrasLogo } from "@/components/FautrasLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, ArrowLeft, LogOut, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PasswordField = ({ label, value, onChange, testId }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <Label className="text-sm font-semibold">{label}</Label>
      <div className="relative mt-2">
        <Input type={show ? "text" : "password"} required value={value} data-testid={testId}
          onChange={(e) => onChange(e.target.value)} className="h-12 pr-11" />
        <button type="button" onClick={() => setShow((v) => !v)} data-testid={`${testId}-toggle`}
          aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#9E7B41]">
          {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );
};

export default function Compte() {
  const { user, loading, logout, authHeader, refresh } = useAuth();
  const navigate = useNavigate();
  const [oldP, setOldP] = useState("");
  const [newP, setNewP] = useState("");
  const [confirmP, setConfirmP] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && !user) { navigate("/login"); return null; }

  const submit = async (e) => {
    e.preventDefault();
    if (newP !== confirmP) { toast.error("Les deux nouveaux mots de passe ne correspondent pas."); return; }
    setBusy(true);
    try {
      if (newP.length < 8) { toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères."); return; }
      const { data } = await axios.post(`${API}/auth/change-password`, { old_password: oldP, new_password: newP }, { headers: authHeader() });
      // Nouveau jeton : les autres sessions sont coupées, celle-ci reste ouverte.
      if (data.token) localStorage.setItem("fautras_token", data.token);
      await refresh();
      toast.success("Mot de passe mis à jour. Vos autres sessions ont été déconnectées.");
      setOldP(""); setNewP(""); setConfirmP("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Modification impossible.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#434343] flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><FautrasLogo dark /></div>
        <div className="bg-white rounded-2xl p-7 sm:p-8 shadow-xl">
          <h1 className="font-heading text-2xl font-extrabold text-[#2b2b2b] flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[#9E7B41]" /> Mon compte
          </h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6">{user?.email}</p>
          {user?.must_change_password && (
            <div className="mb-5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3">
              Pour votre sécurité, veuillez définir votre propre mot de passe.
            </div>
          )}
          <form onSubmit={submit} data-testid="change-password-form" className="space-y-4">
            <PasswordField label="Mot de passe actuel" value={oldP} onChange={setOldP} testId="old-password" />
            <PasswordField label="Nouveau mot de passe" value={newP} onChange={setNewP} testId="new-password" />
            <PasswordField label="Confirmer le nouveau mot de passe" value={confirmP} onChange={setConfirmP} testId="confirm-password" />
            <Button type="submit" disabled={busy} data-testid="change-password-submit"
              className="w-full h-12 bg-[#9E7B41] hover:bg-[#876634] rounded-xl font-semibold">
              {busy ? "Enregistrement…" : "Modifier mon mot de passe"}
            </Button>
          </form>
          <div className="flex items-center justify-between mt-5 text-sm">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/")} data-testid="compte-simulateur" className="text-muted-foreground hover:text-[#9E7B41] inline-flex items-center gap-1">
                <ArrowLeft className="h-4 w-4" /> Simulateur
              </button>
              {user?.role === "admin" ? (
                <button onClick={() => navigate("/admin")} data-testid="compte-admin" className="text-muted-foreground hover:text-[#9E7B41] inline-flex items-center gap-1">
                  Admin
                </button>
              ) : (
                <button onClick={() => navigate("/mes-clients")} data-testid="compte-mesclients" className="text-muted-foreground hover:text-[#9E7B41] inline-flex items-center gap-1">
                  Mes clients
                </button>
              )}
            </div>
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="compte-logout" className="text-muted-foreground hover:text-[#9E7B41] inline-flex items-center gap-1">
              Déconnexion <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
